

import React from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import FormInput from "./FormInput";
import FormSelect from "./FormSelect";
import InvestigatorAvatar from "./InvestigatorAvatar";
import { useOccupations } from "@/hooks/useOccupations";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import SkillTooltip from "./SkillTooltip";
import { useFormData } from "@/stores/formStore";
import { getEraPlaceholders } from "@/utils/era-placeholders";
import { tOccupation } from "@/utils/gamedata-i18n";
import PulpModeToggle from "./PulpModeToggle";

interface IndexFormFieldsProps {
  formData: {
    name: string;
    age: string;
    occupation: string;
    residence: string;
    birthplace: string;
    avatar?: string;
    maxSkillCapacity: number;
  };
  validationErrors: {
    name?: string;
    age?: string;
    occupation?: string;
    residence?: string;
    birthplace?: string;
  };
  handleInputChange: (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOccupationChange?: (value: string) => void;
  handleAvatarChange?: (avatarDataUrl: string) => void;
  handleMaxSkillCapacityChange?: (value: string) => void;
}

const IndexFormFields: React.FC<IndexFormFieldsProps> = ({
  formData,
  validationErrors,
  handleInputChange,
  handleOccupationChange,
  handleAvatarChange,
  handleMaxSkillCapacityChange,
}) => {
  const { formData: storeData } = useFormData();
  const { data: occupations, isLoading: isLoadingOccupations, error: occupationsError } = useOccupations(storeData.era, storeData.socialClass);
  const placeholders = getEraPlaceholders(storeData.era);
  const { t } = useTranslation("widgets");
  return (
    <>
      {handleAvatarChange && (
        <InvestigatorAvatar 
          avatar={formData.avatar}
          onAvatarChange={handleAvatarChange}
        />
      )}
      
      <fieldset className="bg-card border min-w-80 w-full max-w-full text-base text-white font-normal mt-8 p-6 rounded-lg border-border border-solid max-md:px-5 opacity-0 animate-slide-fade-in">
        <legend className="text-white text-lg font-antiqua mb-1 px-1">
          <SkillTooltip content={t("details.fieldsetTooltip")}>
            <span className="cursor-help">{t("details.fieldsetLegend")}</span>
          </SkillTooltip>
        </legend>
        <FormInput
          label={t("details.name")}
          value={formData.name}
          onChange={handleInputChange("name")}
          placeholder={placeholders.details.name}
          error={validationErrors.name}
        />

        <div className="w-full mt-6 max-md:max-w-full">
          <SkillTooltip content={t("details.ageTooltip")}>
            <label className="text-white leading-[1.4] cursor-help inline-block">{t("details.age")}</label>
          </SkillTooltip>
          <input
            type="text"
            inputMode="numeric"
            value={formData.age || ""}
            onChange={handleInputChange("age")}
            placeholder={placeholders.details.age}
            className={`self-stretch flex-1 shrink basis-[0%] bg-card border min-w-60 w-full overflow-hidden leading-none mt-2 px-4 py-3 rounded-lg text-base ${
              validationErrors.age ? "border-destructive" : "border-border"
            } border-solid max-md:max-w-full text-white placeholder:text-muted-foreground`}
          />
          {validationErrors.age && (
            <p className="text-destructive text-xs italic mt-1">{validationErrors.age}</p>
          )}
        </div>

        <div className="w-full mt-6 max-md:max-w-full">
          <SkillTooltip content={t("details.occupationTooltip")}>
            <label className="text-white leading-[1.4] cursor-help inline-block">{t("details.occupation")}</label>
          </SkillTooltip>
          <div className="relative">
            <select
              aria-label={t("details.occupationAria")}
              value={formData.occupation || ""}
              onChange={(e) => handleOccupationChange?.(e.target.value)}
              disabled={isLoadingOccupations}
              className={`bg-card border appearance-none min-w-60 min-h-10 w-full text-white leading-none mt-2 pl-4 pr-10 py-3 rounded-lg text-base ${
                validationErrors.occupation ? "border-destructive" : "border-border"
              } border-solid max-md:max-w-full placeholder:text-muted-foreground ${
                isLoadingOccupations ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <option value="" disabled>
                {isLoadingOccupations ? t("details.occupationLoading") : t("details.occupationPlaceholder")}
              </option>
              {(occupations || []).filter(o => o !== "").map((option) => (
                <option key={option} value={option}>{tOccupation(option)}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 mt-2">
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          {validationErrors.occupation && (
            <p className="text-destructive text-xs italic mt-1">{validationErrors.occupation}</p>
          )}
        </div>

        <FormInput
          label={t("details.residence")}
          value={formData.residence}
          onChange={handleInputChange("residence")}
          placeholder={placeholders.details.residence}
          error={validationErrors.residence}
        />

        <FormInput
          label={t("details.birthplace")}
          value={formData.birthplace}
          onChange={handleInputChange("birthplace")}
          placeholder={placeholders.details.birthplace}
          error={validationErrors.birthplace}
        />

        <div className="w-full mt-6 max-md:max-w-full">
          <SkillTooltip content={t("details.maxSkillTooltip")}>
            <Label className="text-white leading-[1.4] mb-2 max-md:max-w-full cursor-help inline-block">
              {t("details.maxSkillCapacity")}
            </Label>
          </SkillTooltip>
          <RadioGroup
            value={String(formData.maxSkillCapacity)}
            onValueChange={handleMaxSkillCapacityChange}
            className="flex flex-row gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="75" id="capacity-75" className="border-white text-white" />
              <SkillTooltip content={t("details.capacity75Tooltip")}>
                <Label htmlFor="capacity-75" className="text-white cursor-pointer">75</Label>
              </SkillTooltip>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="99" id="capacity-99" className="border-white text-white" />
              <SkillTooltip content={t("details.capacity99Tooltip")}>
                <Label htmlFor="capacity-99" className="text-white cursor-pointer">99</Label>
              </SkillTooltip>
            </div>
          </RadioGroup>
        </div>
      </fieldset>
      <PulpModeToggle />
    </>
  );
};

export default IndexFormFields;
